import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { OneOfPropertyElements } from '@shared/models/project.model';
import { IElementProperty } from '@shared/models/element-property.model';
import { useIsAddedProperty } from '@client/store/ui';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import {
  isElementExpressionProperty,
  isElementSelectProperty,
  isElementSwitchProperty,
  isIconElementProperty,
  isEditablePropertyName,
} from '@shared/helpers/element-property.helpers';
import { getPropertyNameTooltip } from '@shared/helpers/element-property-display-name.helpers';
import { makeStyles } from 'tss-react/mui';
import { isElementQuantityRepeatingChildName } from '@shared/helpers/element-quantity.helpers';
import ElementPropertySelect from './ElementPropertySelect';
import ElementPropertySwitch from './ElementPropertySwitch';
import ElementPropertyExpression from './ElementPropertyExpression';
import ElementPropertyNameInput from './ElementPropertyNameInput';
import ElementPropertyKebabMenu from '@features/projects/menus/ElementPropertyKebabMenu';
import { KebabButtonProps } from '@ui/menus/KebabMenu';

interface ElementPropertyProps {
  property: IElementProperty;
  element: OneOfPropertyElements;
  hideKebabMenu?: boolean;
  hideNameColumn?: boolean;
  showIcon?: boolean;
}

const ElementProperty: React.FC<ElementPropertyProps> = ({
  property,
  element,
  hideKebabMenu,
  hideNameColumn,
  showIcon = false,
}) => {
  const { cx, classes } = useStyles();

  const autoFocus = useIsAddedProperty(property, { autoClearOnUnmount: true });
  const isIconProperty = isIconElementProperty(property);
  const isRepeatingChild = isElementQuantityRepeatingChildName(property.name);
  const isEditableName = isEditablePropertyName(property);

  const [isEditingName, startEditingName, stopEditingName] =
    useBooleanState(autoFocus);

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const input = useMemo(() => {
    if (isElementExpressionProperty(property)) {
      return (
        <ElementPropertyExpression
          property={property}
          element={element}
          readonly={isEditingName}
          onErrorMessageChange={setErrorMessage}
          showIcon={showIcon}
          unitAlignment="none"
        />
      );
    } else if (isElementSelectProperty(property)) {
      return (
        <ElementPropertySelect
          property={property}
          element={element}
          readonly={isEditingName}
        />
      );
    } else if (isElementSwitchProperty(property)) {
      return (
        <ElementPropertySwitch
          property={property}
          element={element}
          readonly={isEditingName}
        />
      );
    }
  }, [element, isEditingName, property, showIcon]);

  const handleCancelNameEdit = useCallback(() => {
    stopEditingName();
    setErrorMessage(undefined);
  }, [stopEditingName, setErrorMessage]);

  const handleNameDoubleClick = useCallback(() => {
    startEditingName();
  }, [startEditingName]);

  useEffect(() => {
    if (autoFocus && isEditableName) {
      startEditingName();
    }
  }, [autoFocus, startEditingName, isEditableName]);

  return (
    <>
      <Tooltip
        title={getPropertyNameTooltip(property, element)}
        placement="left"
        disableInteractive
      >
        <Box
          className={cx(
            classes.container,
            isRepeatingChild ? classes.child : null,
            isIconProperty ? classes.showIcon : null,
          )}
        >
          {!hideNameColumn && !isIconProperty && (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: (property.indentation ?? 0) * 4,
              }}
            >
              <ElementPropertyNameInput
                property={property}
                element={element}
                autoFocus={autoFocus}
                readonly={!isEditingName}
                editing={isEditingName}
                error={!!errorMessage}
                disabled={!isEditablePropertyName(property)}
                onChange={setErrorMessage}
                onDoubleClick={handleNameDoubleClick}
                onSave={handleCancelNameEdit}
                onCancel={handleCancelNameEdit}
              />
            </Box>
          )}
          {!hideKebabMenu && (
            <ElementPropertyKebabMenu
              element={element}
              property={property}
              className={classes.deleteIcon}
              buttonProps={kebabButtonProps}
              onEdit={startEditingName}
            />
          )}
          {/* VALUE INPUT */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isIconProperty ? 'flex-start' : 'flex-end',
            }}
          >
            {input}
          </Box>
        </Box>
      </Tooltip>
    </>
  );
};

const kebabButtonProps: Partial<KebabButtonProps> = {
  verticalIcon: true,
  svgProps: { color: 'secondary' },
};

export const useStyles = makeStyles<void, 'deleteIcon'>()(
  ({ spacing }, _params, classes) => ({
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minWidth: 0,
      paddingTop: spacing(0.5),
      paddingBottom: spacing(0.5),
      gap: spacing(1),

      [`&:hover .${classes.deleteIcon}`]: {
        opacity: 1,
      },
    },
    showIcon: {
      gridTemplateColumns: '20px auto',
      justifyContent: 'start',
      alignItems: 'center',
      gap: spacing(2),
    },
    child: {
      paddingLeft: spacing(3),
      paddingTop: spacing(1.5),
      paddingRight: spacing(2),
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      '&:last-of-type': {
        paddingBottom: spacing(1.5),
      },
    },
    input: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    deleteIcon: {
      opacity: 0,
      transition: 'opacity 0.2s ease',
    },
    errorMessage: {
      gridRow: 2,
      gridColumn: '1 / -1',
    },
  }),
);

export default ElementProperty;
